<script setup lang="ts">
import useLaboratory from "@/laboratory/composables/useLaboratory";
import {laboratoryApi} from "@/container";
import {computed, useId} from "vue";
import useLaboratoryColor from "@/laboratory/composables/useLaboratoryColor";
import useLaboratoryVideos from "@/laboratory/composables/useLaboratoryVideos";
import {Dialog, DialogScrollContent, DialogTrigger} from "@/core/components/ui/dialog";
import {Switch} from "@/core/components/ui/switch";
import {Label} from "@/core/components/ui/label";
import {EyeOff, Play} from "@lucide/vue";

const props = defineProps<{
    laboratoryId: string;
}>();

const {data: laboratory, isLoading} = useLaboratory(props.laboratoryId);
const {data: videos} = useLaboratoryVideos(props.laboratoryId);

const {primaryColor, primaryWithOpacity} = useLaboratoryColor({primary: laboratory.value?.branding?.primary_color});

const switchId = useId();

/**
 * Whether the healthcenter shows the laboratory space to its patients.
 */
const isEnabled = computed(() => laboratory.value?.organization.is_enabled ?? true);

/**
 * Handle laboratory partnership status change.
 *
 * @param newValue
 */
const handleEnabledChange = async (newValue: boolean) => {
    if (laboratory.value?.organization) {
        laboratory.value.organization.is_enabled = newValue;

        await laboratoryApi()
            .updatePartnershipStatus(laboratory.value.organization.id, newValue)
            .then((response) => {
                if (laboratory.value?.organization.is_enabled) {
                    laboratory.value.organization.is_enabled = response.enabled;
                }
            })
            .catch(() => {
                if (laboratory.value?.organization) {
                    laboratory.value.organization.is_enabled = !laboratory.value.organization.is_enabled;
                }
            });
    }
};
</script>

<template>
  <div class="flex items-center gap-4 bg-white rounded-lg shadow-custom py-3 pl-3 pr-4">
    <Dialog v-if="videos?.length">
      <DialogTrigger as-child>
        <button
          type="button"
          aria-label="Voir la vidéo de démonstration"
          class="relative size-16 shrink-0 overflow-hidden rounded-2xl cursor-pointer"
          :style="{backgroundColor: primaryWithOpacity}"
        >
          <img
            alt=""
            src="@/assets/images/iphone-mockup.png"
            class="absolute left-1/2 top-2.5 h-[60px] -translate-x-1/2 object-contain select-none pointer-events-none"
          >
          <span
            class="absolute bottom-1 right-1 flex size-[22px] items-center justify-center rounded-full"
            :style="{backgroundColor: primaryColor}"
          >
            <Play class="size-3 fill-white text-white" />
          </span>
        </button>
      </DialogTrigger>
      <DialogScrollContent>
        <div class="relative inline-block max-w-sm mt-12 mx-auto">
          <img
            alt="iphone-mockup"
            src="@/assets/images/iphone-mockup-transparent.png"
            class="w-full object-contain pointer-events-none"
          >

          <video
            :src="videos[0].url"
            loop
            autoplay
            muted
            style="image-rendering: auto"
            class="select-none object-contain absolute h-[91.7%] w-[84%] left-[calc(50%)] -translate-x-1/2 top-[2.8%] rounded-[1rem]"
          />
        </div>
      </DialogScrollContent>
    </Dialog>
    <div
      v-else
      class="relative size-16 shrink-0 overflow-hidden rounded-2xl"
      :style="{backgroundColor: primaryWithOpacity}"
    >
      <img
        alt=""
        src="@/assets/images/iphone-mockup.png"
        class="absolute left-1/2 top-2.5 h-[60px] -translate-x-1/2 object-contain select-none pointer-events-none"
      >
    </div>

    <div class="flex-1 min-w-0 flex flex-col gap-0.5">
      <p class="font-semibold text-black">
        {{ laboratory?.name }} dans l'app de vos patients
      </p>
      <p v-if="isEnabled" class="text-sm text-gray-500">
        Ils y retrouvent tout l'univers {{ laboratory?.name }}, au sein de votre pharmacie.
      </p>
      <p v-else class="flex items-center gap-1.5 text-sm">
        <EyeOff class="size-4 shrink-0" />
        Masqué&nbsp;: vos patients ne voient plus {{ laboratory?.name }} dans l'app.
      </p>
    </div>

    <router-link
      :to="{name: 'patient-administration.application'}"
      class="shrink-0 text-sm text-gray-500 underline underline-offset-2"
    >
      Gérer l'app de vos patients
    </router-link>

    <div class="h-10 w-px shrink-0 bg-gray-200" />

    <div class="flex shrink-0 items-center gap-3">
      <Label :for="switchId" class="cursor-pointer font-semibold">
        Visible par vos patients
      </Label>
      <Switch
        :id="switchId"
        :model-value="isEnabled"
        :disabled="isLoading"
        :style="isEnabled ? {backgroundColor: primaryColor} : undefined"
        @update:model-value="handleEnabledChange"
      />
    </div>
  </div>
</template>
